import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { ChartQueryParams, ChartTimerName } from '@frontend/chart/types';
import { EntityType } from '@frontend/common';
import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageQueryParams } from '@frontend/package/types';
import { TransactionClient, TransactionTimerClient } from '@frontend/transaction/api';
import { Transaction, TransactionTimer } from '@frontend/transaction/types';

import {
    INSIGHT_HEATMAP_TIME_FIELD_AFTER,
    INSIGHT_HEATMAP_TIME_FIELD_BEFORE,
    InsightHeatmapCellDrilldown,
    InsightHeatmapCellTimeField
} from './insight-heatmap-cell-drilldown';
import { InsightHeatmapCellTimeWindow } from './insight-heatmap-cell-window';

const PAGE_SIZE = 50;

export interface InsightHeatmapCellSummaryRow {
    /** Entity id for openPlatformDetail (transaction or package). */
    id: string;
    entityType: EntityType;
    application: Application;
    feature?: Feature;
    /** Raw timer name when kind is timers. */
    timerName?: ChartTimerName | string;
    trackingNumber?: string | null;
    timestamp: string | null;
}

export interface InsightHeatmapCellSummaryResult {
    rows: InsightHeatmapCellSummaryRow[];
    count: number;
}

export interface FetchInsightHeatmapCellSummaryArgs {
    accountId: string;
    window: InsightHeatmapCellTimeWindow;
    entityParams?: ApiQueryParams<ChartQueryParams>;
}

const baseTimeParams = (args: FetchInsightHeatmapCellSummaryArgs, timeField: InsightHeatmapCellTimeField): ApiQueryParams<string> => ({
    [DefaultQueryParams.ACCOUNT_ID]: args.accountId,
    [DefaultQueryParams.AFTER]: args.window.after,
    [DefaultQueryParams.BEFORE]: args.window.before,
    [INSIGHT_HEATMAP_TIME_FIELD_AFTER]: timeField,
    [INSIGHT_HEATMAP_TIME_FIELD_BEFORE]: timeField,
    [DefaultQueryParams.INDEX]: '0',
    [DefaultQueryParams.SIZE]: String(PAGE_SIZE),
    [DefaultQueryParams.SORTFIELD]: timeField,
    [DefaultQueryParams.SORTDIRECTION]: 'desc',
    ...(args.entityParams ?? {})
});

const timerRow = (timer: TransactionTimer, application: Application, feature?: Feature): InsightHeatmapCellSummaryRow => ({
    id: timer.transaction_id,
    entityType: EntityType.TRANSACTION,
    application: timer.application ?? application,
    feature: timer.feature ?? feature,
    timerName: timer.name,
    // Prefer update_timestamp — matches time_field_after/before used for timer activity cells.
    timestamp: timer.update_timestamp ?? timer.end_timestamp ?? timer.start_timestamp
});

const transactionRow = (transaction: Transaction, application: Application, feature?: Feature): InsightHeatmapCellSummaryRow => ({
    id: transaction.id,
    entityType: EntityType.TRANSACTION,
    application: transaction.application ?? application,
    feature: transaction.feature ?? feature,
    trackingNumber: transaction.tracking_number,
    timestamp: transaction.creation_timestamp
});

const packageRow = (pkg: Package, application: Application, feature?: Feature): InsightHeatmapCellSummaryRow => ({
    id: pkg.transaction_id,
    entityType: EntityType.TRANSACTION,
    application: pkg.application ?? application,
    feature: pkg.feature ?? feature,
    trackingNumber: pkg.id,
    timestamp: pkg.creation_timestamp
});

const toPage = <T>(page: PaginatedResponse<T>): { results: T[]; count: number } => ({
    results: page.results ?? [],
    count: page.count ?? page.results?.length ?? 0
});

export const fetchInsightHeatmapCellSummary = async (
    drilldown: InsightHeatmapCellDrilldown,
    args: FetchInsightHeatmapCellSummaryArgs
): Promise<InsightHeatmapCellSummaryResult> => {
    const params = baseTimeParams(args, drilldown.timeField);

    if (drilldown.kind === 'timers') {
        const page = toPage(
            await TransactionTimerClient.fetchCompletedTimers({
                ...params,
                application: drilldown.application,
                ...(drilldown.feature ? { feature: drilldown.feature } : {}),
                ...(drilldown.timerNames?.length ? { name: drilldown.timerNames } : {})
            })
        );

        return {
            count: page.count,
            rows: page.results.map((timer) => timerRow(timer, drilldown.application, drilldown.feature))
        };
    }

    if (drilldown.kind === 'transactions') {
        const page = toPage(
            await TransactionClient.fetchTransactions({
                ...params,
                application: drilldown.application,
                ...(drilldown.feature ? { feature: drilldown.feature } : {}),
                ...(drilldown.transactionState ? { state: drilldown.transactionState } : {})
            })
        );

        return {
            count: page.count,
            rows: page.results.map((transaction) => transactionRow(transaction, drilldown.application, drilldown.feature))
        };
    }

    // API accepts application/feature; PackageQueryParams enum does not list them yet.
    const page = toPage(
        await PackageClient.fetchPackages({
            ...params,
            application: drilldown.application,
            ...(drilldown.feature ? { feature: drilldown.feature } : {})
        } as ApiQueryParams<DefaultQueryParams | PackageQueryParams>)
    );

    return {
        count: page.count,
        rows: page.results.map((pkg) => packageRow(pkg, drilldown.application, drilldown.feature))
    };
};
